import { z } from 'zod';

/** One person with access in the owner's «Участники». */
export const participantDtoSchema = z.object({
  userId: z.number().int(),
  telegramId: z.number().int(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** The owner and other admins: left out of the returns criterion. */
  isAdmin: z.boolean(),
  accessGrantedAt: z.string(),
  balance: z.number().int(),
  /** The live credits that burn first. */
  nextExpiry: z.object({ credits: z.number().int(), expiresAt: z.string() }).nullable(),
  thoughts: z.number().int(),
  /** Finished results: a build and its rebuilds count once. */
  results: z.number().int(),
  /** Finished results in the first `criteria.returns.days` days of access. */
  resultsEarly: z.number().int(),
  likes: z.number().int(),
  dislikes: z.number().int(),
  costUsd: z.number(),
  lastActiveAt: z.string().nullable(),
  /** The label of the link the person came by (`ig_bio`); null = none. */
  startSource: z.string().nullable(),
});
export type ParticipantDto = z.infer<typeof participantDtoSchema>;

/** The success criteria of the closed launch that need counting (spec 3, #2 and #3). */
export const participantCriteriaSchema = z.object({
  /** Invited people (admins aside) with at least two results in their first `days` days. */
  returns: z.object({
    days: z.number().int(),
    people: z.number().int(),
    withTwo: z.number().int(),
    /** Still inside their first days: may yet get there. */
    stillEarly: z.number().int(),
  }),
  /** The price of a finished reel with its thought, text, takes and every build. */
  reelCost: z.object({
    results: z.number().int(),
    avgUsd: z.number().nullable(),
    maxUsd: z.number().nullable(),
    /** Reels built three times and more: the result and two rebuilds. */
    rebuilt: z.number().int(),
    maxRebuiltUsd: z.number().nullable(),
  }),
});
export type ParticipantCriteria = z.infer<typeof participantCriteriaSchema>;

/** GET /admin/participants */
export const adminParticipantsResponseSchema = z.object({
  participants: z.array(participantDtoSchema),
  criteria: participantCriteriaSchema,
  /** How long added credits live by default: the setting of invite credits. */
  creditDays: z.number().int(),
});
export type AdminParticipantsResponse = z.infer<typeof adminParticipantsResponseSchema>;

/** POST /admin/participants/:id/credits */
export const participantCreditsSchema = z
  .object({
    credits: z.number().int().min(1).max(100_000),
    days: z.number().int().min(1).max(365),
  })
  .strict();
export type ParticipantCreditsInput = z.infer<typeof participantCreditsSchema>;
